import apiClient from './apiClient';

const timerService = {
  /** Fetch current timer state (REST fallback before WebSocket connects). */
  getState: async () => {
    const response = await apiClient.get('/timer/state');
    return response.data.data;
  },

  /**
   * Send a timer command via REST — used as a fallback when the WebSocket is
   * disconnected (e.g. immediately after laptop wakes from sleep).
   * command: 'START' | 'PAUSE' | 'RESUME' | 'COMPLETE' | 'SKIP' | 'RESET'
   */
  sendCommand: async (command) => {
    const response = await apiClient.post('/timer/command', { command });
    return response.data.data; // returns updated TimerStateDTO
  },

  /** Fetch user settings. */
  getSettings: async () => {
    const response = await apiClient.get('/timer/settings');
    return response.data.data;
  },

  /** Update user settings. */
  updateSettings: async (settings) => {
    const response = await apiClient.put('/timer/settings', settings);
    return response.data.data;
  },

  /** Fetch aggregated stats. */
  getStats: async () => {
    const response = await apiClient.get('/timer/stats');
    return response.data.data;
  },

  /** Fetch history between two ISO timestamps. */
  getHistory: async (from, to) => {
    const response = await apiClient.get('/timer/history', {
      params: { from: from.toISOString(), to: to.toISOString() },
    });
    return response.data.data;
  },

  /** Fetch public VAPID key. */
  getVapidKey: async () => {
    const response = await apiClient.get('/timer/push/vapid-key');
    return response.data.data;
  },

  /** Subscribe device endpoint to Web Push. */
  subscribePush: async (subscription) => {
    const response = await apiClient.post('/timer/push/subscribe', subscription);
    return response.data.data;
  },

  /** Unsubscribe device endpoint from Web Push. */
  unsubscribePush: async (endpoint) => {
    const response = await apiClient.post('/timer/push/unsubscribe', { endpoint });
    return response.data.data;
  },

  /** Send immediate test Web Push alert. */
  testPush: async () => {
    const response = await apiClient.post('/timer/push/test');
    return response.data.data;
  },

  // ─ Task management ─────────────────────────────────

  /** List all non-archived tasks. */
  getTasks: async () => {
    const response = await apiClient.get('/timer/tasks');
    return response.data.data;
  },

  /** Create a new task. dto: { name, estimatedPomodoros? } */
  createTask: async (dto) => {
    const response = await apiClient.post('/timer/tasks', dto);
    return response.data.data;
  },

  /** Update task name, estimatedPomodoros, or status. dto fields are all optional. */
  updateTask: async (id, dto) => {
    const response = await apiClient.put(`/timer/tasks/${id}`, dto);
    return response.data.data;
  },

  /** Soft-delete (archive) a task. History is preserved. */
  deleteTask: async (id) => {
    const response = await apiClient.delete(`/timer/tasks/${id}`);
    return response.data.data;
  },

  /** Set a task as the active task on the current session. Returns updated TimerState. */
  activateTask: async (id) => {
    const response = await apiClient.post(`/timer/tasks/${id}/activate`);
    return response.data.data;
  },

  /** Clear the active task from the current session. Returns updated TimerState. */
  deactivateTask: async () => {
    const response = await apiClient.post('/timer/tasks/deactivate');
    return response.data.data;
  },
};

export default timerService;
